'use client';

import * as React from 'react';
import { cn } from '../../utils/cn';

// Inline SVG component to avoid bundling issues
const SelectedCheckIcon = ({ className }: { className?: string }) => (
  <svg
    width="10"
    height="12"
    viewBox="0 0 10 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M0.694824 5.73141C1.80932 6.49427 3.78203 8.74709 4.40185 10.2013C5.34351 6.97106 7.78704 2.32237 9.03861 0.701294"
      stroke="currentColor"
      strokeLinecap="square"
    />
  </svg>
);

export interface SelectableTileProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /**
   * Whether the tile is currently selected
   */
  selected?: boolean;
  /**
   * Callback fired when the selection state changes
   */
  onSelect?: (selected: boolean) => void;
  /**
   * Whether the tile is disabled
   */
  disabled?: boolean;
  /**
   * Optional icon to display above the content
   */
  icon?: React.ReactNode;
  /**
   * Optional className to apply to the icon wrapper
   */
  iconClassName?: string;
  /**
   * Content to display in the tile
   */
  children: React.ReactNode;
}

const SelectableTile = React.forwardRef<HTMLDivElement, SelectableTileProps>(
  (
    {
      className,
      selected = false,
      onSelect,
      disabled = false,
      icon,
      iconClassName,
      children,
      onClick,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;

      onSelect?.(!selected);
      onClick?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      // Handle Space and Enter keys
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onSelect?.(!selected);
      }

      onKeyDown?.(event);
    };

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-pressed={selected}
        aria-disabled={disabled}
        className={cn(
          // Base styles - matching Figma: width 260px, height 64px (hug), gap 8, padding 0
          'h-16 px-6 flex items-center border-solid relative min-w-[150px] gap-2 border transition-colors',
          // Background color - ui-greys/ui-grey-background
          // Border - secondary/red when selected, ui-grey otherwise
          !selected && 'border-velux-ui-grey',
          selected && 'bg-velux-background-grey',
          // Disabled state
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer',
          // Focus styles for accessibility
          // 'focus:outline-none focus:ring-2 focus:ring-velux-professional-red focus:ring-offset-2',
          className
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {/* Red selected indicator - positioned absolutely the left */}
        <div
          className={cn(
            'absolute left-0 h-full w-1 bg-velux-red transition-opacity duration-200',
            selected ? 'opacity-100' : 'opacity-0'
          )}
          aria-hidden="true"
        ></div>

        {/* Checkmark indicator - positioned absolutely right */}
        <div
          className={cn(
            'absolute right-6 transition-opacity duration-200',
            selected ? 'opacity-100' : 'opacity-0'
          )}
          aria-hidden="true"
        >
          <SelectedCheckIcon className="text-velux-black" />
        </div>

        {/* Optional icon */}
        {icon && (
          <div className="flex items-center justify-center text-velux-black">
            <div className={cn("flex items-center justify-center [&>svg]:h-full [&>svg]:w-full", iconClassName)}>
              {icon}
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            'text-center text-sm font-normal text-velux-black',
          )}
        >
          {children}
        </div>
      </div>
    );
  }
);

SelectableTile.displayName = 'SelectableTile';

export { SelectableTile };
